import { useEffect, useState } from 'react'
import { BookOpen, ExternalLink, FileText, Library, ShieldCheck, Upload } from 'lucide-react'
import Button from '../../components/Button'
import FileField from '../../components/FileField'
import FormRow from '../../components/FormRow'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import { Block } from './SettingsParts'
import { getDocumentation, importDocumentation } from './settingsService'
import { documentationStrings as S } from './strings'
import { serverDetail } from '../../lib/i18n'

const GUIDES = [
  { id: 'admin', icon: ShieldCheck, name: S.admin },
  { id: 'user', icon: BookOpen, name: S.user },
]

/**
 * THE PRODUCT DOCUMENTATION, as the contents of its sheet: what is installed, and the
 * signed bundle that changes it. Importing is an action rather than a setting, so it keeps
 * its own button rather than the sheet's Save.
 */
export default function DocumentationCard() {
  const toast = useToast()
  const [guides, setGuides] = useState(null)
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  /* Re-keys the picker after an import, so it lets go of the file it just sent. */
  const [round, setRound] = useState(0)

  const load = () =>
    getDocumentation()
      .then(setGuides)
      .catch(() => setGuides({}))

  useEffect(() => {
    load()
  }, [])

  const upload = async (event) => {
    event.preventDefault()
    if (!file || busy) return
    setBusy(true)
    const outcome = await importDocumentation(file)
    setBusy(false)
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || S.failure)
      return
    }
    toast.success(S.success)
    setFile(null)
    setRound((current) => current + 1)
    load()
  }

  return (
    <>
      <Block title={S.installed} icon={Library}>
        <ul className="set-list">
          {GUIDES.map((guide) => {
            const installed = guides?.[guide.id] ?? null
            const Icon = guide.icon
            return (
              <li key={guide.id} className="set-list__row">
                <Icon className="set-list__icon" size={17} strokeWidth={1.75} aria-hidden="true" />
                <span className="set-list__name">
                  <bdi>{guide.name}</bdi>
                </span>
                {/* Loading: no badge yet rather than a wrong one for a moment. */}
                {guides !== null &&
                  (installed ? (
                    <span className="set-list__actions">
                      <a className="set-link" href={installed.htmlUrl} target="_blank" rel="noreferrer">
                        <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
                        {S.open}
                      </a>
                      <a className="set-link" href={installed.pdfUrl} target="_blank" rel="noreferrer">
                        <FileText size={14} strokeWidth={2} aria-hidden="true" />
                        {S.pdf}
                      </a>
                    </span>
                  ) : (
                    <StatusBadge tone="dark">{S.notImported}</StatusBadge>
                  ))}
              </li>
            )
          })}
        </ul>
      </Block>

      <Block title={S.importTitle} icon={Upload}>
        <form className="set-import" onSubmit={upload}>
          <FormRow label={S.file} hint={S.hint}>
            {(id) => <FileField key={round} id={id} accept=".zip,application/zip" onChange={setFile} formats={['ZIP']} />}
          </FormRow>
          <Button type="submit" busy={busy} disabled={!file}>
            {S.upload}
          </Button>
        </form>
      </Block>
    </>
  )
}
